// external libraries
import { lazy, Suspense } from "react";
import classNames from "classnames";

// hooks and state
import { useAuth } from "../contexts/AuthContext";
import { useDashboardContext } from "../contexts/DashboardProvider";

// components
import { Tabs } from "@happy-doc/shared-components";

// lazy-loaded components
const InsightsOverview = lazy(() => import("./dashboard/InsightsOverview"));
const SentimentAnalysis = lazy(() => import("./dashboard/SentimentAnalysis"));
const StandardOfCare = lazy(() => import("./dashboard/StandardOfCare"));

// types and utilities
import { TabIds } from "@happy-doc/shared-types";
import { Loading } from "@happy-doc/insights-shared";

// render the tab based on the tab id
const renderTab = (tab: string) => {
    switch (tab) {
        case TabIds.SentimentAnalysis:
            return (
                <Suspense fallback={<Loading />}>
                    <SentimentAnalysis />
                </Suspense>
            );
        case TabIds.Overview:
            return (
                <Suspense fallback={<Loading />}>
                    <InsightsOverview />
                </Suspense>
            );
        case TabIds.StandardOfCare:
            return (
                <Suspense fallback={<Loading />}>
                    <StandardOfCare />
                </Suspense>
            );
        default:
            return (
                <Suspense fallback={<Loading />}>
                    <InsightsOverview />
                </Suspense>
            )
    }
}

function Dashboard() {
    const { account } = useAuth();
    const { dashboardStateContext } = useDashboardContext();
    const { handleTabClick, secondaryTabs, primaryTabs, activeTabSlugs } = dashboardStateContext;

    return (
        <div>
            <div className={classNames("pt-8 bg-white", primaryTabs.length > 1 ? "" : "pb-4")}>
                <div className="pl-8">
                    <h1 className="font-semibold text-3xl text-gray-900">
                        {"View key insights" + (account?.clinic_name ? ` for ${account?.clinic_name}` : "")}
                    </h1>
                    <p className="text-gray-600 text-sm">
                        Access a detailed overview of key metrics and visit patient outcomes.
                    </p>
                </div>

                {primaryTabs.length > 1 && (
                    <div className="pt-4">
                        <Tabs
                            tabs={primaryTabs}
                            changeTab={(tabId) => handleTabClick(tabId, null)}
                            paddingHorizontal="px-4"
                        />
                        {secondaryTabs.length > 0 && (
                            <div className="pt-2 pb-4 border-b border-gray-300">
                                <Tabs
                                    tabs={secondaryTabs}
                                    changeTab={(tabId) => handleTabClick(primaryTabs.find(t => t.isActive)?.id || "", tabId)}
                                    showBorder={false}
                                    paddingHorizontal="px-4"
                                    variant={"highlight" as any}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="p-4">
                {renderTab(activeTabSlugs.primary || TabIds.Overview)}
            </div>
        </div>
    )
}

export default Dashboard;